import './team-detail.scss';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import BackArrowIcon from 'assets/images/iPrise/BackArrow.svg';
import EditIcon from 'assets/images/iPrise/Edit.svg';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useCreateItem,
  useFetchList,
  useFetchSingleData,
  usePatchItem,
  useUpdateItem,
} from 'services/common/react-query-common-service';
import { useAppSelector } from 'store/hooks';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../../common/components/StylishLoader';
import { TeamUserRoles } from '../../enum/TeamUserRoles';
import AddRecruiterModal from '../components/AddRecruiterModal';
import AddTeamMemberModal from '../components/AddTeamMemberModal';
import CreateTeamModal from '../components/CreateTeamModal';

const RECRUITER_TYPE = 'recruiter';

interface DraggableRecruiterRowProps {
  recruiter: any;
  index: number;
  onDrop: (recyclerId: string, newPriority: number) => void;
  getDisplayName: (
    firstName?: string,
    lastName?: string,
    fullName?: string,
  ) => string;
}

const DraggableRecruiterRow: React.FC<DraggableRecruiterRowProps> = ({
  recruiter,
  index,
  onDrop,
  getDisplayName,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);

  const [{ isDragging }, drag] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: { id: recruiter.id, index },
    type: RECRUITER_TYPE,
  });

  const [, drop] = useDrop({
    accept: RECRUITER_TYPE,
    drop: (item: { id: string; index: number }) => {
      // Call API with dragged item's ID and new priority (index + 1)
      onDrop(item.id, index + 1);
    },
    hover: (item: { id: string; index: number }) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
    },
  });

  drag(drop(ref));

  return (
    <TableRow
      ref={ref}
      style={{
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
      }}>
      <TableCell className="order-cell">
        <Box className="order-content">
          <DragIndicatorIcon className="drag-icon" />
          <Typography className="order-number">{index + 1}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        {getDisplayName(
          recruiter.user?.firstName || recruiter.firstName,
          recruiter.user?.lastName || recruiter.lastName,
          recruiter.name,
        )}
      </TableCell>
      <TableCell>{recruiter.user?.login?.email || recruiter.email}</TableCell>
      <TableCell>{recruiter.role || recruiter.user?.teamUserRole}</TableCell>
      <TableCell>
        <Chip
          label={
            recruiter.user?.status ?? recruiter.status ? 'Active' : 'Inactive'
          }
          className={`status-chip ${
            !(recruiter.user?.status ?? recruiter.status) ? 'inactive' : ''
          }`}
        />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" className="more-btn">
          <MoreHorizIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const TeamDetail: React.FC = observer(() => {
  const { userInfo: userDetail }: any = useAppSelector(
    (store: any) => store.userLogin,
  );
  const isBusinessHead =
    userDetail?.teamUserRole === TeamUserRoles.BUSINESS_HEAD;
  const isManager = userDetail?.teamUserRole === TeamUserRoles.MANAGER;
  const isManagement = userDetail?.teamUserRole === TeamUserRoles.MANAGEMENT;
  const isTeamLead = userDetail?.teamUserRole === TeamUserRoles.TEAM_LEADER;

  const { managementTeamsService } = useStore();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Use team data from MobX (already set when clicking from teams list)
  // This allows instant header display while API call is in progress
  const teamData = managementTeamsService.teamInfo;

  // Build query params based on role
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (isBusinessHead && userDetail?.id) {
      params.append('businessHeadId', String(userDetail.id));
    } else if (isManager && userDetail?.id) {
      params.append('managerId', String(userDetail.id));
    } else if (isTeamLead && userDetail?.id) {
      params.append('teamLeadId', String(userDetail.id));
    }
    return params.toString();
  }, [isBusinessHead, isManager, isTeamLead, userDetail]);

  // Special ID for useFetchSingleData to include params
  const teamInfoIdWithParams = useMemo(() => {
    const baseId = id || '';
    return queryParams ? `${baseId}&${queryParams}` : baseId;
  }, [id, queryParams]);

  // Fetch team info (header data) - fresh from API
  const { data: teamInfoResp, isLoading: isLoadingTeamInfo } =
    useFetchSingleData<any>(
      ['team-info', id, queryParams],
      teamInfoIdWithParams,
      MASTER_ENDPOINT.Teams,
      {
        enabled: !!id,
        refetchOnMount: 'always', // Refetch on every mount
      },
    );

  // Update MobX when fresh team data arrives from API
  useEffect(() => {
    if (teamInfoResp?.data && teamInfoResp?.data?.[0]) {
      managementTeamsService.setTeamInfo(teamInfoResp.data?.[0]);
    }
  }, [teamInfoResp, managementTeamsService]);

  // State declarations
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isAddRecruiterModalOpen, setIsAddRecruiterModalOpen] = useState(false);
  const [parentTeamMemberId, setParentTeamMemberId] = useState<string | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showMoreTags, setShowMoreTags] = useState(false);
  const tagsRef = useRef<HTMLDivElement>(null);
  const [isTagsOverflowing, setIsTagsOverflowing] = useState(false);

  // Check for tags overflow
  useEffect(() => {
    if (tagsRef.current) {
      // Check if scrollHeight is more than roughly 2 lines (52px)
      // We use a small timeout to ensure DOM is rendered
      const checkOverflow = () => {
        if (tagsRef.current) {
          setIsTagsOverflowing(tagsRef.current.scrollHeight > 52);
        }
      };
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }
  }, [teamData?.customers, teamData?.teamCustomers, teamData?.teamJobSources]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch team members (both team leads and support users) - single API call
  const { data: teamMembersResp, isLoading: isLoadingMembers } =
    useFetchList<any>(
      ['team-members', id, debouncedSearch, queryParams],
      `${String(MASTER_ENDPOINT.Teams)}/${String(
        id || '',
      )}/members?search=${String(debouncedSearch)}${
        queryParams ? `&${String(queryParams)}` : ''
      }`,
      {
        enabled: !!id,
        refetchOnMount: 'always', // Refetch on every mount
      },
    );

  const getInitials = (firstName?: string, lastName?: string) => {
    return (
      `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}` || '??'
    );
  };

  const formatTime = (time?: string) => {
    if (!time) return '';
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${String(formattedHour).padStart(2, '0')}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const { mutateAsync: updateTeam } = useUpdateItem();
  const { mutateAsync: addTeamMembers } = useCreateItem();
  const { mutateAsync: patchTeamStatus } = usePatchItem();
  const { mutate: changePriority } = useCreateItem();
  const queryClient = useQueryClient();

  const handleChangePriority = useCallback(
    (recruiterId: string, newPriority: number) => {
      changePriority(
        {
          data: {
            id: recruiterId,
            newPriority,
          },
          endpoint: `${MASTER_ENDPOINT.Teams}/change-priority`,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['team-members', id],
            });
          },
        },
      );
    },
    [changePriority, queryClient, id],
  );

  const getDisplayName = (
    firstName?: string,
    lastName?: string,
    fullName?: string,
  ) => {
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
    return fullName || 'N/A';
  };
  const [expandedLeads, setExpandedLeads] = useState<Record<number, boolean>>(
    {},
  );

  // Extract team leads and support users from single API response
  const teamLeads = useMemo(
    () => teamMembersResp?.teamLeaders || [],
    [teamMembersResp],
  );

  const supportUsers = useMemo(
    () => teamMembersResp?.supporters || [],
    [teamMembersResp],
  );

  // Sort recruiters by priority for Team Lead view
  const sortedRecruiters = useMemo(() => {
    const recruiters = teamLeads?.[0]?.recruiters ?? [];
    return [...recruiters].sort((a, b) => {
      const priorityA = a.priority ?? Number.MAX_SAFE_INTEGER;
      const priorityB = b.priority ?? Number.MAX_SAFE_INTEGER;
      return priorityA - priorityB;
    });
  }, [teamLeads]);

  const location = useLocation();

  const handleBack = useCallback(() => {
    const basePath = location.pathname.split('/')[1];
    navigate(`/${basePath}/teams`);
  }, [navigate, location]);

  const handleOpenAddMemberModal = useCallback(() => {
    setIsAddMemberModalOpen(true);
  }, []);

  const handleCloseAddMemberModal = useCallback(() => {
    setIsAddMemberModalOpen(false);
  }, []);

  const handleAddMembers = useCallback(
    async (data: { teamLeaderIds: string[]; supporterIds: string[] }) => {
      await addTeamMembers({
        data: {
          supporterIds: data.supporterIds,
          teamId: id,
          teamLeaderIds: data.teamLeaderIds,
        },
        endpoint: 'teams/add-team-leader',
      });
      // Refetch team members after successful addition
      await queryClient.invalidateQueries({ queryKey: ['team-members', id] });
      // Modal handles closing itself on success
    },
    [id, addTeamMembers, queryClient],
  );

  const handleOpenAddRecruiterModal = useCallback((parentId: string) => {
    setParentTeamMemberId(parentId);
    setIsAddRecruiterModalOpen(true);
  }, []);

  const handleCloseAddRecruiterModal = useCallback(() => {
    setIsAddRecruiterModalOpen(false);
    setParentTeamMemberId(null);
  }, []);

  const handleAddRecruiters = useCallback(
    async (recruiterIds: string[]) => {
      if (!parentTeamMemberId) return;

      await addTeamMembers({
        data: {
          parentId: parentTeamMemberId,
          teamId: id,
          userIds: recruiterIds,
        },
        endpoint: 'teams/add-team-member',
      });

      // Refetch team members after successful addition
      await queryClient.invalidateQueries({ queryKey: ['team-members', id] });
      // Modal handles closing itself on success
    },
    [parentTeamMemberId, id, addTeamMembers, queryClient],
  );

  const toggleLeadExpand = (leadId: number) => {
    setExpandedLeads((prev) => ({
      ...prev,
      [leadId]: !prev[leadId],
    }));
  };

  const handleStatusChange = useCallback(
    async (event: any) => {
      const newStatus = event.target.value;
      const isActive = newStatus === 'active';

      try {
        await patchTeamStatus({
          data: { isActive },
          endpoint: `teams/${id}/status`,
        });
        // Refetch team info after successful status update
        await queryClient.invalidateQueries({ queryKey: ['team-info', id] });
      } catch (error) {
        console.error('Failed to update team status:', error);
      }
    },
    [id, patchTeamStatus, queryClient],
  );

  const handleOpenEditModal = () => setIsEditModalOpen(true);
  const handleCloseEditModal = () => setIsEditModalOpen(false);

  const handleEditSubmit = async (data: any) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { customerIds, teamCustomers, ...restData } = data;

      await updateTeam({
        data: {
          ...restData,
          teamCustomers: teamCustomers || [],
        },
        endpoint: MASTER_ENDPOINT.Teams,
        id: id || '',
      });
      // Refetch team info after successful update
      await queryClient.invalidateQueries({ queryKey: ['team-info', id] });
      handleCloseEditModal();
    } catch (error) {
      console.error('Failed to update team:', error);
    }
  };

  if (isLoadingTeamInfo && !teamData) {
    return <StylishLoader text="Loading team details..." />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className="team-detail">
        {/* Header Section */}
        <Box className="team-detail-header">
          <Box className="header-left">
            <IconButton className="back-button" onClick={handleBack}>
              <img src={BackArrowIcon} alt="Back" />
            </IconButton>
            <Box className="team-info">
              <Typography className="team-name">
                {teamData?.teamName} - {teamData?.teamCode}
              </Typography>
              <Typography className="working-hours">
                Working Hours • {formatTime(teamData?.workingHoursStart)} -{' '}
                {formatTime(teamData?.workingHoursEnd)}
              </Typography>
              <Box
                ref={tagsRef}
                className={`team-tags ${
                  showMoreTags ? 'expanded' : 'collapsed'
                }`}>
                {teamData?.teamJobSources?.map((jobSource: any) => {
                  const sourceName = String(
                    jobSource.jobSource?.name || jobSource.name || '',
                  );
                  const isBeeline = sourceName.toLowerCase() === 'beeline';
                  const customerNames =
                    isBeeline && Array.isArray(teamData?.teamCustomers)
                      ? teamData.teamCustomers
                          .map((c: any) =>
                            String(c.customer?.name || c.name || ''),
                          )
                          .filter(Boolean)
                          .join(', ')
                      : '';
                  const label = customerNames
                    ? `${String(sourceName)} (${String(customerNames)})`
                    : sourceName;

                  return (
                    <Chip
                      key={jobSource.id || jobSource.jobSourceId || sourceName}
                      className="team-tag"
                      label={label}
                    />
                  );
                })}
              </Box>
              {isTagsOverflowing && (
                <Box className="tags-action-area">
                  <Button
                    className="show-more-tags-btn"
                    onClick={() => setShowMoreTags(!showMoreTags)}>
                    {showMoreTags ? 'Show Less' : 'Show More'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <Box className="header-right">
            <Box className="team-members-preview">
              <Box className="member-item">
                <Box className="member-avatar">
                  <Typography className="avatar-text">
                    {getInitials(
                      teamData?.businessHead?.firstName,
                      teamData?.businessHead?.lastName,
                    )}
                  </Typography>
                </Box>
                <Box className="member-details">
                  <Typography className="member-name">
                    {teamData?.businessHead?.firstName}{' '}
                    {teamData?.businessHead?.lastName}
                  </Typography>
                  <Typography className="member-role">Business Head</Typography>
                </Box>
              </Box>

              <Box className="member-item">
                <Box className="member-avatar">
                  <Typography className="avatar-text">
                    {getInitials(
                      teamData?.teamManager?.firstName,
                      teamData?.teamManager?.lastName,
                    )}
                  </Typography>
                </Box>
                <Box className="member-details">
                  <Typography className="member-name">
                    {teamData?.teamManager?.firstName}{' '}
                    {teamData?.teamManager?.lastName}
                  </Typography>
                  <Typography className="member-role">Manager</Typography>
                </Box>
              </Box>
            </Box>

            {isManagement && (
              <Select
                value={teamData?.isActive ? 'active' : 'inactive'}
                onChange={handleStatusChange}
                className={`status-select ${
                  teamData?.isActive ? 'active' : 'inactive'
                }`}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{
                  PaperProps: {
                    className: 'status-select-menu',
                  },
                }}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            )}

            {isManagement && (
              <Button
                className="edit-button"
                variant="outlined"
                onClick={handleOpenEditModal}>
                <img src={EditIcon} alt="Edit" />
                Edit Team
              </Button>
            )}
          </Box>
        </Box>

        {/* Team Content */}
        <Box className="team-content">
          <Box className="members-content">
            <Box className="content-header">
              <Typography className="content-title">
                Team Members{' '}
                {isTeamLead
                  ? `(${String((teamLeads?.[0]?.recruiters ?? []).length)})`
                  : `(${String(teamLeads.length + supportUsers.length)})`}
              </Typography>
              <Box className="header-actions">
                <TextField
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  size="small"
                />
                <IconButton className="filter-btn">
                  <FilterListIcon />
                </IconButton>
                {isManagement && (
                  <Button
                    variant="contained"
                    className="add-tl-btn"
                    onClick={handleOpenAddMemberModal}>
                    Add TL / Support
                  </Button>
                )}
              </Box>
            </Box>

            {/* Team Leads Section */}
            {isLoadingMembers ? (
              <StylishLoader text="Loading team members..." className="table" />
            ) : isTeamLead ? (
              <Box className="team-leads-section">
                <Box className="team-lead-card">
                  <Box className="lead-details" sx={{ borderTop: 'none' }}>
                    <TableContainer className="recruiter-table-container">
                      <Table className="recruiter-table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedRecruiters.map((recruiter, index) => (
                            <TableRow key={recruiter.id}>
                              {/* <TableCell className="order-cell">
                              <Box className="order-content">
                                <DragIndicatorIcon className="drag-icon" />
                                <Typography className="order-number">
                                  {index + 1}
                                </Typography>
                              </Box>
                            </TableCell> */}
                              <TableCell>
                                {getDisplayName(
                                  recruiter.user?.firstName ||
                                    recruiter.firstName,
                                  recruiter.user?.lastName ||
                                    recruiter.lastName,
                                  recruiter.name,
                                )}
                              </TableCell>
                              <TableCell>
                                {recruiter.user?.login?.email ||
                                  recruiter.email}
                              </TableCell>
                              <TableCell>
                                {recruiter.role || recruiter.user?.teamUserRole}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    recruiter.user?.status ?? recruiter.status
                                      ? 'Active'
                                      : 'Inactive'
                                  }
                                  className={`status-chip ${
                                    !(
                                      recruiter.user?.status ?? recruiter.status
                                    )
                                      ? 'inactive'
                                      : ''
                                  }`}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" className="more-btn">
                                  <MoreHorizIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!teamLeads[0]?.recruiters ||
                            teamLeads[0].recruiters.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <Typography
                                  sx={{
                                    color: '#5e6c84',
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    py: 3,
                                  }}>
                                  No recruiters found.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            ) : (
              <>
                <Box className="team-leads-section">
                  {teamLeads.map((lead) => (
                    <Box key={lead.id} className="team-lead-card">
                      <Box
                        className="lead-header"
                        onClick={() => toggleLeadExpand(lead.id)}
                        sx={{ cursor: 'pointer' }}>
                        <Box className="lead-left">
                          <IconButton
                            className={`expand-btn ${
                              expandedLeads[lead.id] ? 'expanded' : ''
                            }`}
                            size="small"
                            onClick={(e) => e.stopPropagation()}>
                            <ExpandMoreIcon />
                          </IconButton>
                          <Box className="lead-avatar">
                            {lead.user?.imageId ? (
                              <img
                                src={
                                  lead.user.imageId
                                    ? `${process.env.REACT_APP_APIURL}${
                                        MASTER_ENDPOINT.Files
                                      }/${String(lead.user.imageId)}`
                                    : ''
                                }
                                alt={getDisplayName(
                                  lead.user?.firstName,
                                  lead.user?.lastName,
                                )}
                              />
                            ) : (
                              <Typography className="avatar-initials">
                                {getInitials(
                                  lead.user?.firstName,
                                  lead.user?.lastName,
                                )}
                              </Typography>
                            )}
                          </Box>
                          <Box className="lead-info">
                            <Typography className="lead-name">
                              {getDisplayName(
                                lead.user?.firstName,
                                lead.user?.lastName,
                              )}
                            </Typography>
                            <Typography className="lead-role">
                              {lead.role || lead.user?.teamUserRole}
                            </Typography>
                          </Box>
                        </Box>
                        <Box className="lead-right">
                          <Typography className="recruiter-count">
                            Recruiter: {lead.recruitersCount || 0}
                          </Typography>
                          {isManagement && (
                            <IconButton
                              className="add-recruiter-btn"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAddRecruiterModal(lead.userId);
                              }}>
                              <AddIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Collapse
                        in={expandedLeads[lead.id]}
                        timeout="auto"
                        unmountOnExit>
                        <Box className="lead-details">
                          <TableContainer className="recruiter-table-container">
                            <Table className="recruiter-table">
                              <TableHead>
                                <TableRow>
                                  <TableCell className="order-cell">
                                    Order
                                  </TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Email</TableCell>
                                  <TableCell>Role</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right" />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {[...(lead.recruiters || [])]
                                  .sort((a, b) => {
                                    const priorityA =
                                      a.priority ?? Number.MAX_SAFE_INTEGER;
                                    const priorityB =
                                      b.priority ?? Number.MAX_SAFE_INTEGER;
                                    return priorityA - priorityB;
                                  })
                                  .map((recruiter, index) => (
                                    <DraggableRecruiterRow
                                      key={recruiter.id}
                                      recruiter={recruiter}
                                      index={index}
                                      onDrop={handleChangePriority}
                                      getDisplayName={getDisplayName}
                                    />
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>

                {/* Support Users Section */}
                <Box className="support-section">
                  <Typography className="section-title">
                    Support Users
                  </Typography>
                  <Box sx={{ padding: '12px' }}>
                    <TableContainer className="support-table-container">
                      <Table className="support-table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {supportUsers.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>
                                {getDisplayName(
                                  user.user?.firstName || user.firstName,
                                  user.user?.lastName || user.lastName,
                                  user.name,
                                )}
                              </TableCell>
                              <TableCell>
                                {user.user?.login?.email || user.email}
                              </TableCell>
                              <TableCell>
                                {user.role || user.user?.teamUserRole}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    user.user?.status ?? user.status
                                      ? 'Active'
                                      : 'Inactive'
                                  }
                                  className={`status-chip ${
                                    !(user.user?.status ?? user.status)
                                      ? 'inactive'
                                      : ''
                                  }`}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" className="more-btn">
                                  <MoreHorizIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>

        <AddTeamMemberModal
          open={isAddMemberModalOpen}
          onClose={handleCloseAddMemberModal}
          onAddMembers={handleAddMembers}
          teamId={id}
        />

        {/* Add Recruiter Modal */}
        <AddRecruiterModal
          open={isAddRecruiterModalOpen}
          onClose={handleCloseAddRecruiterModal}
          onAddRecruiters={handleAddRecruiters}
          teamId={id}
        />

        {/* Edit Team Modal */}
        {teamData && (
          <CreateTeamModal
            open={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSubmit={handleEditSubmit}
            initialValues={{
              businessHeadId: teamData.businessHeadId || '',
              customerIds:
                teamData.teamCustomers?.map((c: any) => c.customerId || c.id) ||
                [],
              jobSourceIds:
                teamData.teamJobSources?.map((c: any) => c.jobSourceId) || [],
              teamCode: teamData.teamCode || '',
              teamCustomers:
                teamData.teamCustomers?.map((c: any) => ({
                  customerId: String(c.customerId || c.id || ''),
                  jobSourceId: String(c.jobSourceId || ''),
                })) || [],
              teamManagerId: teamData.teamManagerId || '',
              teamName: teamData.teamName || '',
              teamType: teamData.teamType || '',
              workingHoursEnd: teamData.workingHoursEnd || '17:00:00',
              workingHoursStart: teamData.workingHoursStart || '08:00:00',
            }}
            initialLabels={{
              businessHead: teamData.businessHead
                ? `${String(teamData.businessHead.firstName || '')} ${String(
                    teamData.businessHead.lastName || '',
                  )}`.trim()
                : undefined,
              customers: teamData.teamCustomers?.map((c: any) => ({
                label: String(c.customer?.name || c.name || ''),
                value: String(c.customerId || c.id || ''),
              })),
              customersByJobSource: (() => {
                const map: Record<
                  string,
                  Array<{ label: string; value: string }>
                > = {};
                (teamData.teamCustomers || []).forEach((c: any) => {
                  const jsId = String(c.jobSourceId || '');
                  if (!jsId) return;
                  if (!map[jsId]) map[jsId] = [];
                  map[jsId].push({
                    label: String(c.customer?.name || c.name || ''),
                    value: String(c.customerId || c.id || ''),
                  });
                });
                return map;
              })(),
              jobSources: teamData.teamJobSources?.map((c: any) => ({
                label: String(c.jobSource?.name || c.name || ''),
                value: String(c.jobSourceId),
              })),
              teamManager: teamData.teamManager
                ? `${String(teamData.teamManager.firstName || '')} ${String(
                    teamData.teamManager.lastName || '',
                  )}`.trim()
                : undefined,
            }}
            mode="edit"
            title="Edit Team"
            subtitle="Update team information and mappings"
            submitButtonText="Update Team"
          />
        )}
      </Box>
    </DndProvider>
  );
});

export default TeamDetail;
